import { CustomInput } from 'components';
import { CreateEditJobNavEnum, CreateJobButtonIds, JOB_ATTRIBUTE_TYPE } from 'containers/jobs/constant';
import { CreateJobContextType } from 'containers/jobs/types';
import { useAppDispatch, useAppSelector } from 'hooks';
import React, { FC, useContext } from 'react';
import {
  selectAutoFilledData,
  selectInitialJobReferenceNumber,
  selectJobReferenceNumber,
  updateJobReferenceNumber
} from '../createJobSlice';
import { useCheckJobReferenceNumberMutation } from 'containers/jobs/api';
import { selectCurrentTeam, showNotifier } from 'containers/app/appSlice';
import { NotifierTypes } from 'constants/notifierConstants';
import AutoFilledIndicator from 'components/auto-filled-indicator/AutoFilledIndicator';

interface MoreOptionsSectionProps {
  className?: string;
  context: React.Context<CreateJobContextType>;
}

const MoreOptionsSection: FC<MoreOptionsSectionProps> = ({ context }) => {
  const { selectedJobTab, setSelectedJobTab } = useContext<CreateJobContextType>(context);

  const selectedJobReferenceNumber = useAppSelector(selectJobReferenceNumber);
  const initialJobReferenceNumber = useAppSelector(selectInitialJobReferenceNumber);
  const currentTeam = useAppSelector(selectCurrentTeam);
  const autoFilledData = useAppSelector(selectAutoFilledData);
  const dispatch = useAppDispatch();

  const [checkJobRefNumberQuery, { originalArgs: jobRefMutationArguments }] = useCheckJobReferenceNumberMutation();

  const isTabSelected = selectedJobTab === CreateEditJobNavEnum.MORE_OPTIONS;

  const updateSelectedJobTab = () => {
    setSelectedJobTab(CreateEditJobNavEnum.MORE_OPTIONS);
  };

  const handleReferenceNumberChange = value => {
    dispatch(updateJobReferenceNumber(value));
  };

  const checkJobReferenceNumber = value => {
    const jobRefNumberResult = checkJobRefNumberQuery({
      teamId: currentTeam.id,
      body: { value, attribute: JOB_ATTRIBUTE_TYPE.REFERENCE_ID }
    }).unwrap();
    return jobRefNumberResult;
  };

  const handleJobReferenceNumberCheck = async (e: FocusEvent) => {
    const elementRelatedTarget: HTMLInputElement = e.relatedTarget as HTMLInputElement;
    const jobsButtonTargetArrayString: Array<string> = Object.values(CreateJobButtonIds);

    if (jobsButtonTargetArrayString.includes(elementRelatedTarget?.id)) {
      return;
    }
    const jobRefNumber = selectedJobReferenceNumber.trim();
    if (
      jobRefNumber !== '' &&
      jobRefNumber !== jobRefMutationArguments?.body.value &&
      jobRefNumber !== initialJobReferenceNumber.trim()
    ) {
      const { data } = await checkJobReferenceNumber(jobRefNumber);
      const { isExists } = data ?? {};
      if (isExists) {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: `Job reference number '${selectedJobReferenceNumber}' already exists in a different job.`
            },
            type: NotifierTypes.WARNING,
            showClose: true
          })
        );
      }
    }
  };

  return (
    <div>
      <div className="mb-2 text-xl font-bold text-heading">More options</div>
      <div
        className={`relative rounded-md border py-6 px-8 ${isTabSelected ? 'border-primaryBtn' : 'border-borderGray'}`}>
        <div className="mb-2 text-17px font-semibold text-heading">
          Job reference number{' '}
          <AutoFilledIndicator
            isAutoFilled={autoFilledData?.referenceNo && autoFilledData?.referenceNo == selectedJobReferenceNumber}
          />
        </div>
        <div>
          <CustomInput
            id="create-job-notes"
            maxLength={100}
            name="notes"
            placeholder="Enter job reference number"
            value={selectedJobReferenceNumber ?? ''}
            onChange={handleReferenceNumberChange}
            className="mb-4 py-[15px]"
            onFocus={updateSelectedJobTab}
            onBlur={handleJobReferenceNumberCheck}
          />
        </div>
      </div>
    </div>
  );
};

export default MoreOptionsSection;
